import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ViewsModel } from '../shared/project/cards/views-model';
import { CardsModel, Tab, TabAddress } from '../shared/project/cards/cards-model';
import { MainFcstWalkModel, FcstWalkTreeNode } from '../shared/project/cards/fcst-walk-model';
import { RenameViewModalComponent } from './rename-view-modal.component';
import { DataService } from '../shared/network/data.service';

@Component({
    selector: 'app-tabs-tree-view',
    templateUrl: './tabs-tree-view.component.html',
    styleUrls: ['./tabs-tree-view.component.scss']
})
export class TabsTreeViewComponent {
    @Input() viewsModel: ViewsModel | null = null;
    @Input() mainFcstWalkModel: MainFcstWalkModel | null = null;
    @Input() cardsModel: CardsModel | null = null;
    @Input() isViewingForecastWalk: boolean = false;
    @Input() isViewingProjectOverview: boolean = false;
    @Input() projectId: string = '';
    @Input() copiedTab: TabAddress | undefined = undefined;

    @Output() duplicateProjectOverview = new EventEmitter<void>();
    @Output() duplicateView = new EventEmitter<CardsModel>();
    @Output() deleteView = new EventEmitter<CardsModel>();
    @Output() closeTab = new EventEmitter<{viewId: string; tabId: string}>();
    @Output() selectTabForCopying = new EventEmitter<{ view: CardsModel; tab: Tab | undefined }>();
    @Output() cancelCopyMode = new EventEmitter<void>();
    @Output() deleteFcstNode = new EventEmitter<FcstWalkTreeNode>();

    constructor(public dialog: MatDialog, private dataService: DataService) {}

    // Query params methods
    public getMainTabQueryParams(view: CardsModel) {
        // guiTab/viewId are cleared so deep-link params don't linger (links use 'merge')
        // and override the tab's own saved gui-tab on subsequent tree navigation.
        return view.tabUId ? { projectId: this.projectId, tabuid: view.tabUId, fcstWalk: null, guiTab: null, viewId: null } : null;
    }

    public getTabQueryParams(view: CardsModel, tab: Tab) {
        return tab.tabUId ? { projectId: this.projectId, tabuid: tab.tabUId, fcstWalk: null, guiTab: null, viewId: null } : null;
    }

    public getProjectOverviewQueryParams() {
        // clear tab/deep-link params the same way the view links do, so nothing lingers via 'merge'.
        // On the FOR side the node leads to the main forecast walk (the parent of the forecast tabs under it), otherwise to VAR.
        return { projectId: this.projectId, tabuid: null, fcstWalk: this.isViewingForecastWalk ? 'main' : null, guiTab: null, viewId: null };
    }

    public getFcstWalkTabQueryParams(node: FcstWalkTreeNode) {
        if (!this.mainFcstWalkModel || !node) {
            return null;
        }
        
        const tabIndex = this.mainFcstWalkModel.tabs.findIndex(tab => 
            tab.path.length === node.tab.path.length && 
            tab.path.every((val, index) => val === node.tab.path[index])
        );
        
        return tabIndex >= 0 ? { projectId: this.projectId, fcstWalk: tabIndex.toString() } : null;
    }

    public isActiveFcstWalkNode(node: FcstWalkTreeNode): boolean {
        if (!this.isViewingForecastWalk || !this.mainFcstWalkModel || this.mainFcstWalkModel.selectedTabIx === undefined) {
            return false;
        }
        const selectedTab = this.mainFcstWalkModel.tabs[this.mainFcstWalkModel.selectedTabIx];
        if (!selectedTab) {
            return false;
        }
        return selectedTab.path.length === node.tab.path.length && 
                     selectedTab.path.every((val, index) => val === node.tab.path[index]);
    }

    public onDuplicateProjectOverview() {
        this.duplicateProjectOverview.emit();
    }

    public onDuplicateView(view: CardsModel) {
        this.duplicateView.emit(view);
    }

    public onDeleteView(view: CardsModel) {
        this.deleteView.emit(view);
    }

    public renameView(view: CardsModel) {
        const dialogRef = this.dialog.open(RenameViewModalComponent, {
            data: { currentName: view.name || '' }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                view.name = result;
                this.dataService.post('processing/renameView', {
                    viewId: view.viewId,
                    name: result
                });
            }
        });
    }

    public onSelectTabForCopying(view: CardsModel, tab?: Tab) {
        this.selectTabForCopying.emit({ view, tab });
    }

    public onCancelCopyMode() {
        this.cancelCopyMode.emit();
    }

    public onDeleteTabClick(viewId: string, tabId: string) {
        if (this.viewsModel) {
            const view = this.viewsModel.views.find(v => v.viewId === viewId);
            if (view) {
                this.removeTabFromTree(view.hierarchicalTabs, tabId);
            }
        }
        this.closeTab.emit({ viewId, tabId });
    }

    private removeTabFromTree(tabs: Tab[], tabId: string): boolean {
        for (let i = 0; i < tabs.length; i++) {
            if (tabs[i].id === tabId) {
                tabs.splice(i, 1);
                return true;
            }
            if (tabs[i].children && this.removeTabFromTree(tabs[i].children, tabId)) {
                return true;
            }
        }
        return false;
    }

    public isSelectedForCopying(view: CardsModel, tab?: Tab): boolean {
        if (!this.copiedTab) {
            return false;
        }
        const tabId = tab?.id;
        return this.copiedTab.viewId === view.viewId && this.copiedTab.tabId === tabId;
    }

    public onDeleteFcstNodeClick(node: FcstWalkTreeNode): void {
        if (this.mainFcstWalkModel?.tabsTree) {
            this.removeFcstNodeFromTree(this.mainFcstWalkModel.tabsTree, node);
        }

        // Only update selectedTabIx if the deleted node or a descendant is selected
        const selectedTab = this.mainFcstWalkModel?.selectedTabIx !== undefined
            ? this.mainFcstWalkModel.tabs[this.mainFcstWalkModel.selectedTabIx]
            : undefined;
        const selectionAffected = selectedTab &&
            selectedTab.path.length >= node.tab.path.length &&
            node.tab.path.every((v, i) => v === selectedTab.path[i]);

        if (selectionAffected) {
            const parentPath = node.tab.path.slice(0, -1);
            if (parentPath.length === 0) {
                this.mainFcstWalkModel.selectedTabIx = undefined;
            } else {
                const parentTabIx = this.mainFcstWalkModel.tabs.findIndex(t =>
                    t.path.length === parentPath.length &&
                    t.path.every((v, i) => v === parentPath[i])
                );
                if (parentTabIx >= 0) {
                    this.mainFcstWalkModel.selectedTabIx = parentTabIx;
                }
            }
        }

        this.deleteFcstNode.emit(node);
    }

    private removeFcstNodeFromTree(nodes: FcstWalkTreeNode[], target: FcstWalkTreeNode): boolean {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i] === target) {
                nodes.splice(i, 1);
                return true;
            }
            if (nodes[i].children?.length && this.removeFcstNodeFromTree(nodes[i].children, target)) {
                return true;
            }
        }
        return false;
    }
}
